import type { FormAnswers, FormDefinition } from '@serviceportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { FormRenderer } from '@/components/form-renderer';
import { PageHeader } from '@/components/ui/form-field';
import { Alert, Card, Skeleton } from '@/components/ui/primitives';
import { api, ApiError, errorMessage } from '@/lib/api';

export function usePortalForms() {
  return useQuery({ queryKey: ['portal', 'forms'], queryFn: () => api<{ slug: string; name: string; description: string | null }[]>('/portal/forms') });
}

export function PortalFormPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const { data: form, error } = useQuery({
    queryKey: ['portal', 'form', slug],
    queryFn: () => api<{ name: string; description: string | null; definition: FormDefinition }>(`/portal/forms/${slug}`),
    retry: false,
  });
  const submit = useMutation({
    mutationFn: (data: FormAnswers) => api<{ ticketId: string; ticketNumber: string }>(`/portal/forms/${slug}/submit`, { method: 'POST', body: { data } }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['portal', 'tickets'] });
      toast.success(`${form?.definition.confirmation.title ?? 'Vielen Dank!'} Ihre Vorgangsnummer: ${r.ticketNumber}`);
      navigate(`/portal/tickets/${r.ticketId}`);
    },
    onError: (e) => {
      if (e instanceof ApiError && e.details) setServerErrors(Object.fromEntries(e.details.map((d) => [d.path, d.message])));
    },
  });

  if (error) return <Alert>Dieses Formular ist nicht verfügbar.</Alert>;
  if (!form) return <Skeleton className="h-96" />;
  return (
    <>
      <Link to="/portal/new" className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="size-4" /> Neue Anfrage
      </Link>
      <PageHeader title={form.name} description={form.description ?? undefined} />
      <Card className="p-5 sm:p-8">
        <FormRenderer
          definition={form.definition}
          submitting={submit.isPending}
          serverErrors={serverErrors}
          error={submit.error ? errorMessage(submit.error) : null}
          onSubmit={async (data) => {
            setServerErrors({});
            await submit.mutateAsync(data).catch(() => undefined);
          }}
          uploadFile={async (file) => {
            const fd = new window.FormData();
            fd.append('file', file);
            return api<{ token: string; name: string }>(`/portal/forms/${slug}/files`, { method: 'POST', form: fd });
          }}
        />
      </Card>
    </>
  );
}
